import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import { api, type ReferenceFeaturesApi } from "@/lib/api";

export const MUNICIPAL_LAYER_CODE = "goiania-arvores";
export const MUNICIPAL_COLOR = "#7c3aed";

export type MunicipalLayerStatus =
  | { state: "loading" }
  | { state: "error"; message: string }
  | { state: "ready"; mode: "clusters" | "points"; count: number; truncated: boolean };

const ESCAPE: Record<string, string> = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };
const esc = (v: unknown) => String(v ?? "").replace(/[&<>"']/g, (c) => ESCAPE[c]);
const shown = (v: unknown) => v !== null && v !== undefined && String(v).trim() !== "" && String(v).trim() !== "-";

/** Campos destacados no popup. Os codigos vem da fonte sem dicionario oficial. */
const HIGHLIGHT: [string, string][] = [
  ["id", "Identificador no cadastro"],
  ["cdespecie", "Código da espécie"],
  ["porte", "Porte"],
  ["idade", "Idade"],
  ["cap", "CAP"],
  ["dtlevanta", "Data do levantamento"],
  ["observacao", "Observação"],
];

const NOTICE =
  "Origem: Prefeitura de Goiânia (cadastro ArcGIS), levantamento de 1998–2000. " +
  "Dado de referência, não é avaliação FloraIQ. Os códigos não têm dicionário oficial e a " +
  "posição pode ter deslocamento de dezenas de metros.";

function recordHtml(attrs: Record<string, unknown>): string {
  const main = HIGHLIGHT.filter(([k]) => shown(attrs[k]))
    .map(([k, label]) => `<tr><td style="color:#6b7280;padding-right:8px">${esc(label)}</td><td>${esc(attrs[k])}</td></tr>`)
    .join("");
  const all = Object.keys(attrs)
    .sort()
    .filter((k) => shown(attrs[k]))
    .map((k) => `<tr><td style="color:#6b7280;padding-right:8px;font-family:monospace">${esc(k)}</td><td>${esc(attrs[k])}</td></tr>`)
    .join("");
  return `<div style="font-size:11px;min-width:220px;max-width:280px">
    <strong style="font-size:13px;color:${MUNICIPAL_COLOR}">Árvore do cadastro municipal</strong>
    <table style="margin-top:6px;border-collapse:collapse">${main}</table>
    <details style="margin-top:6px"><summary style="cursor:pointer;color:#374151">Todos os atributos</summary>
      <div style="max-height:180px;overflow:auto;margin-top:4px"><table style="border-collapse:collapse">${all}</table></div>
    </details>
    <p style="margin-top:8px;padding:6px;background:#f5f3ff;border-radius:4px;color:#4c1d95;line-height:1.35">${esc(NOTICE)}</p>
  </div>`;
}

function clusterIcon(count: number): L.DivIcon {
  const size = count >= 1000 ? 40 : count >= 100 ? 32 : count >= 10 ? 26 : 20;
  const label = count >= 1000 ? `${(count / 1000).toFixed(count >= 10000 ? 0 : 1)}k` : String(count);
  return L.divIcon({
    html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${MUNICIPAL_COLOR}cc;border:2px solid white;color:white;font-size:10px;font-weight:600;display:flex;align-items:center;justify-content:center;box-shadow:0 1px 3px rgba(0,0,0,.3)">${label}</div>`,
    className: "",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

/** Arvores do cadastro da Prefeitura, carregadas pela area visivel (agrupadas abaixo do zoom 17). */
export default function MunicipalTreesLayer({ onStatus }: { onStatus: (s: MunicipalLayerStatus) => void }) {
  const map = useMap();
  const statusRef = useRef(onStatus);
  statusRef.current = onStatus;

  useEffect(() => {
    // Pane proprio acima dos poligonos (bairros, regioes, quadras), senao o clique cai no poligono.
    const PANE = "municipalTrees";
    if (!map.getPane(PANE)) map.createPane(PANE).style.zIndex = "450";
    const renderer = L.canvas({ padding: 0.3, pane: PANE });
    const group = L.layerGroup().addTo(map);
    let controller: AbortController | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const draw = (data: ReferenceFeaturesApi) => {
      group.clearLayers();
      if (data.mode === "clusters") {
        for (const c of data.items) {
          const m = L.marker([c.lat, c.lon], { icon: clusterIcon(c.count), keyboard: false });
          m.on("click", () => map.setView([c.lat, c.lon], Math.min(map.getZoom() + 2, 19)));
          m.bindTooltip(`${c.count.toLocaleString("pt-BR")} árvore(s) do cadastro municipal`);
          group.addLayer(m);
        }
        statusRef.current({ state: "ready", mode: "clusters", count: data.items.reduce((a, c) => a + c.count, 0), truncated: false });
        return;
      }
      for (const p of data.items) {
        const m = L.circleMarker([p.lat, p.lon], {
          renderer, pane: PANE, radius: 5, color: "#ffffff", weight: 1.5, fillColor: MUNICIPAL_COLOR, fillOpacity: 0.9,
        });
        // Popup do mapa, nao do ponto: abrir o popup move o mapa, a camada recarrega e
        // apagaria o ponto (e o popup preso a ele) antes de o registro chegar.
        m.on("click", () => {
          const popup = L.popup({ maxWidth: 300, autoPanPaddingTopLeft: [20, 120] })
            .setLatLng([p.lat, p.lon])
            .setContent("<div style='font-size:11px'>Carregando…</div>")
            .openOn(map);
          api
            .referenceRecord(MUNICIPAL_LAYER_CODE, p.id)
            .then((r) => popup.setContent(recordHtml(r.attributes)))
            .catch(() => popup.setContent("<div style='font-size:11px'>Não foi possível carregar o registro.</div>"));
        });
        group.addLayer(m);
      }
      statusRef.current({ state: "ready", mode: "points", count: data.items.length, truncated: data.truncated });
    };

    const load = () => {
      controller?.abort();
      controller = new AbortController();
      const b = map.getBounds();
      statusRef.current({ state: "loading" });
      api
        .referenceFeatures(MUNICIPAL_LAYER_CODE, [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()], Math.round(map.getZoom()), controller.signal)
        .then(draw)
        .catch((err: unknown) => {
          if (err instanceof DOMException && err.name === "AbortError") return;
          group.clearLayers();
          statusRef.current({ state: "error", message: err instanceof Error ? err.message : "Falha ao carregar a camada" });
        });
    };
    const schedule = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(load, 300);
    };

    load();
    map.on("moveend", schedule);
    return () => {
      map.off("moveend", schedule);
      if (timer) clearTimeout(timer);
      controller?.abort();
      group.remove();
      renderer.remove(); // o canvas nao sai sozinho do pane
    };
  }, [map]);

  return null;
}
